import * as THREE from 'three';
import { withFogOfWar } from '../environment/fog-of-war.js';

/**
 * What a Shadow trails about itself: a cold pool of dark on the ground under it, and wisps of black smoke that curl up off its smoke and
 * thin away. Both are one draw each, moved entirely in their shaders; the animator only sets their uniforms (`shadow-animator.ts`).
 */
export interface ShadowShroud {
  pool: THREE.Mesh; wisps: THREE.Mesh;
  uniforms: {
    uTime: { value: number };
    /** 0..1+: how dark and wide the pool spreads. */
    uPool: { value: number };
    /** 0..1+: how many wisps show and how thick; above 1 for the bursts of rising and coming apart. */
    uWisps: { value: number };
    /** How fast the wisps climb (1 at rest). */
    uLift: { value: number };
    /** Where the glide pulls the smoke, in the figure's own frame. */
    uStream: { value: THREE.Vector2 };
  };
}

const NOISE = /* glsl */ `
  float shHash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
  float shNoise(vec2 p) {
    vec2 i = floor(p), f = fract(p); f = f * f * (3. - 2. * f);
    return mix(mix(shHash(i), shHash(i + vec2(1, 0)), f.x), mix(shHash(i + vec2(0, 1)), shHash(i + vec2(1, 1)), f.x), f.y);
  }
  float shFbm(vec2 p) { return .55 * shNoise(p) + .3 * shNoise(p * 2.03 + 7.1) + .15 * shNoise(p * 4.1 - 3.7); }`;

const POOL_VERTEX = /* glsl */ `
  #include <common>
  #include <fog_pars_vertex>
  varying vec2 vUv;
  void main() {
    vUv = uv * 2. - 1.;
    vec4 mvPosition = modelViewMatrix * vec4(position, 1.);
    gl_Position = projectionMatrix * mvPosition;
    #include <fog_vertex>
  }`;
const POOL_FRAGMENT = /* glsl */ `
  #include <common>
  #include <fog_pars_fragment>
  uniform float uTime; uniform float uPool; varying vec2 vUv;
  ${NOISE}
  void main() {
    float r = length(vUv), a = atan(vUv.y, vUv.x);
    // A slow inward swirl: the dark turns and draws towards the centre, ragged at its rim.
    vec2 swirl = vec2(cos(a + r * 2.6 - uTime * .35), sin(a + r * 2.6 - uTime * .35)) * r;
    float n = shFbm(swirl * 2.4 + vec2(0., uTime * .12)), edge = .62 + .3 * n;
    float body = 1. - smoothstep(edge * .3, edge, r / max(.05, min(1., uPool)));
    float alpha = min(1., body * 1.5) * (.75 + .25 * n) * min(1., uPool);
    gl_FragColor = vec4(vec3(.006, .005, .009), alpha);
    #include <fog_fragment>
  }`;

const WISP_VERTEX = /* glsl */ `
  #include <common>
  #include <fog_pars_vertex>
  uniform float uTime; uniform float uWisps; uniform float uLift; uniform vec2 uStream;
  attribute vec2 aCorner; attribute vec4 aSeed;
  varying vec2 vCorner; varying float vAlpha; varying float vSeed;
  void main() {
    // Each wisp loops: it leaves the body at a seeded height and angle, curls outward and up, swells and thins away.
    float life = fract(uTime * (.22 + .16 * aSeed.x) * uLift + aSeed.y);
    float angle = aSeed.z * 6.2832 + life * (1.2 + aSeed.w) * (aSeed.x > .5 ? 1. : -1.);
    float start = .12 + aSeed.w * 1.45, radius = mix(.13, .1, aSeed.w) + life * (.14 + .2 * aSeed.x);
    vec3 centre = vec3(cos(angle) * radius, start + life * (.55 + .5 * aSeed.x), sin(angle) * radius * .85);
    centre.xz += uStream * life * (.6 + start * .3);
    float size = (.1 + .3 * life) * (.8 + .5 * aSeed.x);
    // The thinnest wisps show first as the density rises, so a calm Shadow sheds a few and a burst sheds them all.
    float shown = smoothstep(aSeed.x * .9, aSeed.x * .9 + .25, uWisps);
    vAlpha = sin(3.14159 * life) * shown * min(1.5, uWisps) * (1. - .5 * life);
    vCorner = aCorner; vSeed = aSeed.z;
    vec4 mvPosition = modelViewMatrix * vec4(centre, 1.);
    float turn = aSeed.y * 6.2832 + life * 1.5;
    mvPosition.xy += mat2(cos(turn), sin(turn), -sin(turn), cos(turn)) * aCorner * size * vec2(1., 1.6);
    gl_Position = projectionMatrix * mvPosition;
    #include <fog_vertex>
  }`;
const WISP_FRAGMENT = /* glsl */ `
  #include <common>
  #include <fog_pars_fragment>
  uniform float uTime; varying vec2 vCorner; varying float vAlpha; varying float vSeed;
  ${NOISE}
  void main() {
    float r = length(vCorner);
    // Torn, streaky smoke rather than a round puff.
    float n = shFbm(vCorner * vec2(1.6, .8) * 2. + vec2(vSeed * 13., uTime * .4));
    float alpha = (1. - smoothstep(.25, 1., r)) * smoothstep(.3, .75, n) * vAlpha * .7;
    if (alpha < .004) discard;
    gl_FragColor = vec4(vec3(.03, .027, .038), alpha);
    #include <fog_fragment>
  }`;

const WISPS = 48;

function shaderMaterial(vertexShader: string, fragmentShader: string, uniforms: ShadowShroud['uniforms']) {
  return withFogOfWar(new THREE.ShaderMaterial({
    vertexShader, fragmentShader, uniforms: { ...THREE.UniformsUtils.clone(THREE.UniformsLib.fog), ...uniforms },
    transparent: true, depthWrite: false, fog: true, side: THREE.DoubleSide,
  }));
}

export function createShadowShroud(seed: number): ShadowShroud {
  const uniforms: ShadowShroud['uniforms'] = { uTime: { value: 0 }, uPool: { value: 0 }, uWisps: { value: 0 }, uLift: { value: 1 }, uStream: { value: new THREE.Vector2() } };
  const poolMaterial = shaderMaterial(POOL_VERTEX, POOL_FRAGMENT, uniforms);
  poolMaterial.polygonOffset = true; poolMaterial.polygonOffsetFactor = -4; poolMaterial.polygonOffsetUnits = -4;
  const pool = new THREE.Mesh(new THREE.PlaneGeometry(2.1, 2.1).rotateX(-Math.PI / 2), poolMaterial);
  pool.name = 'shadow-pool'; pool.position.y = .05; pool.renderOrder = 1;

  const corners = [-1, -1, 1, -1, 1, 1, -1, 1], positions = new Float32Array(WISPS * 12), corner = new Float32Array(WISPS * 8), seeds = new Float32Array(WISPS * 16), index: number[] = [];
  let state = Math.floor(seed * 1e6) | 1;
  const random = () => { state = (state * 16807) % 2147483647; return state / 2147483647; };
  for (let i = 0; i < WISPS; i++) {
    const s = [random(), random(), random(), random()];
    for (let k = 0; k < 4; k++) { corner.set(corners.slice(k * 2, k * 2 + 2), (i * 4 + k) * 2); seeds.set(s, (i * 4 + k) * 4); }
    index.push(i * 4, i * 4 + 1, i * 4 + 2, i * 4, i * 4 + 2, i * 4 + 3);
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute('aCorner', new THREE.BufferAttribute(corner, 2)); geometry.setAttribute('aSeed', new THREE.BufferAttribute(seeds, 4));
  geometry.setIndex(index);
  // The quads are placed in the shader: cull against the reach of the smoke, not the empty positions.
  geometry.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, 1.1, 0), 1.8);
  const wisps = new THREE.Mesh(geometry, shaderMaterial(WISP_VERTEX, WISP_FRAGMENT, uniforms));
  wisps.name = 'shadow-wisps'; wisps.frustumCulled = true; wisps.renderOrder = 2;
  return { pool, wisps, uniforms };
}
